import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Language, TextAlignment } from '../../shared/enum/EnumRepository';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { PollQuestion } from '../../shared/dto/EntityRepository';
import { PollSelectedValue } from '../../shared/dto/EntityRepository';
import { UniversalCheckbox } from '../../shared/component/UniversalCheckbox';

export interface QuestionSectionProps {
  question: PollQuestion;
  pollData: Record<string, any>;
  onPollDataChange: (field: string, value: any) => void;
  language?: Language;
}

const QuestionSectionSingle: React.FC<QuestionSectionProps> = ({
  question,
  onPollDataChange,
  language
}) => {
  const { id, title, options = [] } = question;
  const [selectedValue, setSelectedValue] = useState<PollSelectedValue>({option: "", value: ""});
  const [otherValue, setOtherValue] = useState<string>('');

  useEffect(() => {
    onPollDataChange(id, selectedValue);
  }, [selectedValue]);


  const isSelected = (index: number, optionValue: string) => {
    if (optionValue === 'other') {
      return selectedValue.option === 'other';
    } else {
      return selectedValue.option === index.toString();
    }
  };

  const handleOptionChange = (index: number, optionValue: string) => {
    setSelectedValue({
      option: optionValue === 'other' ? 'other' : index.toString(), 
      value: optionValue === 'other' ? otherValue : optionValue,
    });
  };

  const handleOtherInputChange = (value: string) => {
    setOtherValue(value);
    setSelectedValue({
      option: 'other', 
      value: value,
    });
  };

  return (
    <StyledQuestionSection data-question-id={id}>
      <QuestionTitle>{title}</QuestionTitle>
      {options.map((option, index) => (
        <div key={index}>
          <CheckboxComp>
            <UniversalCheckbox
              type="radio"
              name={title}
              checked={isSelected(index + 1, option.value)}
              onChange={() => handleOptionChange(index + 1, option.value)}
            />
            {option.label}
          </CheckboxComp>
          {option.hasOther && isSelected(index + 1, option.value) && (
            <OtherInputWrapper>
              <HeadlessInput
                type="text"
                value={otherValue}
                placeholder={language === Language.KR 
                  ? `내용을 입력해주세요 (${option.otherMaxLength || 50}자 이내)`
                  : `Please enter details (within ${option.otherMaxLength || 50} characters)`
                }
                handleChange={(e) => handleOtherInputChange(e.target.value)}
                StyledInput={OtherInput}
                $alignment={TextAlignment.LEFT}
                maxLimit={option.otherMaxLength || 50}
              />
            </OtherInputWrapper>
          )}
        </div>
      ))}
    </StyledQuestionSection>
  );
};

export default QuestionSectionSingle;

const StyledQuestionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
`;

const QuestionTitle = styled.h3`
  ${({ theme }) => theme.typography.Body_03_1};
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const CheckboxComp = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 10px;
  user-select: none;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const OtherInputWrapper = styled.div`
  margin-top: 8px;
`;

const OtherInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_02};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.color_Gray_04};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.color_Gray_05};
  }
`;
